'use client'

import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { ChevronDown, Search, X } from 'lucide-react'
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface DropdownItem {
  id: string
  name: string
  [key: string]: any
}

interface SearchableDropdownProps {
  items: DropdownItem[]
  selectedId: string
  onSelect: (id: string) => void
  onCustomSelect?: (query: string) => void
  placeholder: string
  searchPlaceholder: string
  icon: ReactNode
  isLoading?: boolean
  disabled?: boolean
  className?: string
  selectedColor?: 'blue' | 'pink' | 'green'
  searchKey?: string
  renderItem?: (item: DropdownItem, isSelected: boolean) => ReactNode
  openDirection?: 'auto' | 'up' | 'down'
  closeOnScroll?: boolean
}

export function SearchableDropdown({
  items,
  selectedId,
  onSelect,
  onCustomSelect,
  placeholder,
  searchPlaceholder,
  icon,
  isLoading = false,
  disabled = false,
  className = '',
  selectedColor = 'blue',
  searchKey = 'name',
  renderItem,
  openDirection = 'auto',
  closeOnScroll = false,
}: SearchableDropdownProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const [buttonPosition, setButtonPosition] = useState<DOMRect | null>(null)
  const [shouldOpenUp, setShouldOpenUp] = useState(false)

  const handleCustomSubmit = useCallback(() => {
    if (!onCustomSelect || !searchQuery.trim()) return
    onCustomSelect(searchQuery.trim())
    setIsOpen(false)
    setSearchQuery('')
    setButtonPosition(null)
    setShouldOpenUp(false)
  }, [onCustomSelect, searchQuery])
  const dropdownRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)

  // Filter items based on search query
  const filteredItems = searchQuery.trim()
    ? items.filter((item) => item[searchKey]?.toLowerCase().includes(searchQuery.toLowerCase()))
    : items

  // Note: We now use manual positioning with shouldOpenUp instead of useDropdownPosition

  const toggleDropdown = useCallback(() => {
    if (disabled || isLoading) return

    if (!isOpen) {
      // Calculate button position when opening
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect()
        setButtonPosition(rect)

        // Determine dropdown direction
        const estimatedDropdownHeight = Math.min(400, (filteredItems.length + 1) * 70 + 140) // Approximate height for two-line items
        let openUp = false

        if (openDirection === 'up') {
          openUp = true
        } else if (openDirection === 'down') {
          openUp = false
        } else {
          // Auto: Check available space below vs above
          const spaceBelow = window.innerHeight - rect.bottom
          const spaceAbove = rect.top
          openUp = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow
        }

        setShouldOpenUp(openUp)
      }
    }

    setIsOpen(!isOpen)
    // Clear search when closing
    if (isOpen) {
      setSearchQuery('')
      setButtonPosition(null)
      setShouldOpenUp(false)
    }
  }, [isOpen, disabled, isLoading, openDirection, filteredItems.length])

  const handleBackdropClick = useCallback(() => {
    setIsOpen(false)
    setSearchQuery('')
    setButtonPosition(null)
    setShouldOpenUp(false)
  }, [])

  const handleItemSelect = useCallback(
    (itemId: string) => {
      onSelect(itemId)
      setIsOpen(false)
      setSearchQuery('')
      setButtonPosition(null)
      setShouldOpenUp(false)
    },
    [onSelect]
  )

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setSearchQuery('')
        setButtonPosition(null)
        setShouldOpenUp(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // Update dropdown position on scroll
  useEffect(() => {
    if (!isOpen) return

    const handleScroll = () => {
      if (closeOnScroll) {
        // Old behavior: close dropdown
        setIsOpen(false)
        setSearchQuery('')
        setButtonPosition(null)
        setShouldOpenUp(false)
      } else {
        // New behavior: update position
        if (buttonRef.current) {
          const rect = buttonRef.current.getBoundingClientRect()
          setButtonPosition(rect)

          // Recalculate dropdown direction if needed
          const estimatedDropdownHeight = Math.min(400, (filteredItems.length + 1) * 70 + 140)
          let openUp = false

          const spaceBelow = window.innerHeight - rect.bottom
          const spaceAbove = rect.top
          openUp = spaceBelow < estimatedDropdownHeight && spaceAbove > spaceBelow

          setShouldOpenUp(openUp)
        }
      }
    }

    // Find all scrollable parent elements and attach scroll listeners
    const scrollableParents: Element[] = []
    let element = buttonRef.current?.parentElement

    while (element && element !== document.body) {
      const { overflow, overflowY, overflowX } = window.getComputedStyle(element)
      if (
        overflow === 'auto' ||
        overflow === 'scroll' ||
        overflowY === 'auto' ||
        overflowY === 'scroll' ||
        overflowX === 'auto' ||
        overflowX === 'scroll'
      ) {
        scrollableParents.push(element)
        element.addEventListener('scroll', handleScroll, { passive: true })
      }
      element = element.parentElement
    }

    // Also listen to window scroll
    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollableParents.forEach((el) => {
        el.removeEventListener('scroll', handleScroll)
      })
      window.removeEventListener('scroll', handleScroll)
    }
  }, [isOpen, closeOnScroll, filteredItems.length])

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'pink':
        return {
          bg: 'bg-pink-50',
          text: 'text-pink-700',
          ring: 'focus-visible:ring-pink-500',
          iconSelected: 'text-pink-500',
          dotSelected: 'bg-pink-500',
        }
      case 'green':
        return {
          bg: 'bg-green-50',
          text: 'text-green-700',
          ring: 'focus-visible:ring-green-500',
          iconSelected: 'text-green-500',
          dotSelected: 'bg-green-500',
        }
      default: // blue
        return {
          bg: 'bg-blue-50',
          text: 'text-blue-700',
          ring: 'focus-visible:ring-blue-500',
          iconSelected: 'text-blue-500',
          dotSelected: 'bg-blue-500',
        }
    }
  }

  const colorClasses = getColorClasses(selectedColor)
  const selectedItem = items.find((item) => item.id === selectedId)

  return (
    <div className={cn('relative', className)} ref={dropdownRef}>
      {/* Portal for Backdrop and Dropdown Menu */}
      {typeof window !== 'undefined' &&
        createPortal(
          <>
            {/* Backdrop */}
            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                  className="fixed inset-0 bg-transparent"
                  style={{ zIndex: 9998 }}
                  onClick={handleBackdropClick}
                />
              )}
            </AnimatePresence>

            {/* Dropdown Menu */}
            <AnimatePresence>
              {isOpen && buttonPosition && (
                <motion.div
                  initial={{
                    opacity: 0,
                    y: shouldOpenUp ? 8 : -8,
                    scale: 0.96,
                  }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{
                    opacity: 0,
                    y: shouldOpenUp ? 8 : -8,
                    scale: 0.96,
                  }}
                  transition={{
                    duration: 0.2,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  className="fixed z-[9999] overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl"
                  style={{
                    top: shouldOpenUp ? 'auto' : buttonPosition.bottom + 4,
                    bottom: shouldOpenUp ? window.innerHeight - buttonPosition.top + 4 : 'auto',
                    left: buttonPosition.left,
                    width: buttonPosition.width,
                    maxHeight: Math.min(
                      400,
                      shouldOpenUp
                        ? buttonPosition.top - 70
                        : window.innerHeight - buttonPosition.bottom - 70
                    ),
                    backdropFilter: 'blur(8px)',
                  }}
                  onWheel={(e) => e.stopPropagation()}
                  onTouchMove={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  {/* Search Input - Always shown */}
                  <div className="border-b border-gray-100 p-3">
                    <div className="flex items-center gap-2">
                      <div className="relative flex-1">
                        <input
                          type="text"
                          placeholder={searchPlaceholder}
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] py-3 pl-9 pr-3 text-base text-gray-900 placeholder-gray-500 caret-blue-500 transition-colors focus:border-[#B0B0B0] focus:caret-blue-600 focus:outline-none"
                          onClick={(e) => e.stopPropagation()}
                          onMouseDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (
                              e.key === 'Enter' &&
                              searchQuery.trim() &&
                              onCustomSelect &&
                              filteredItems.length === 0
                            ) {
                              e.preventDefault()
                              handleCustomSubmit()
                            }
                          }}
                          autoFocus
                        />
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
                      </div>
                      <button
                        onClick={() => {
                          setIsOpen(false)
                          setSearchQuery('')
                          setButtonPosition(null)
                          setShouldOpenUp(false)
                        }}
                        className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    {searchQuery && filteredItems.length === 0 && !onCustomSelect && (
                      <p className="mt-1 text-xs text-gray-500">No items found</p>
                    )}
                  </div>

                  <div
                    className="scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-400 hover:scrollbar-thumb-gray-500 overflow-y-auto p-2 pb-2"
                    style={{
                      overscrollBehavior: 'contain',
                      maxHeight: Math.min(
                        330,
                        shouldOpenUp
                          ? buttonPosition.top - 160 // Account for search bar and spacing when opening up
                          : window.innerHeight - buttonPosition.bottom - 160 // Account for search bar and spacing when opening down
                      ),
                    }}
                  >
                    <div className="space-y-1">
                      {/* Regular items */}
                      {filteredItems.map((item) => {
                        const isSelected = item.id === selectedId

                        return (
                          <motion.button
                            key={item.id}
                            onClick={() => handleItemSelect(item.id)}
                            whileHover={
                              !isSelected ? { backgroundColor: 'rgb(249 250 251)' } : undefined
                            }
                            whileTap={!isSelected ? { scale: 0.98 } : undefined}
                            className={cn(
                              'flex w-full items-center gap-3 rounded-lg p-3 text-left transition-colors',
                              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1',
                              colorClasses.ring,
                              {
                                [colorClasses.bg + ' ' + colorClasses.text]: isSelected,
                                'text-gray-700 hover:bg-gray-50': !isSelected,
                                'cursor-not-allowed': isSelected,
                              }
                            )}
                          >
                            {renderItem ? (
                              renderItem(item, isSelected)
                            ) : (
                              <>
                                <div className="flex-shrink-0">
                                  <span
                                    className={cn('h-4 w-4', {
                                      [colorClasses.iconSelected]: isSelected,
                                      'text-gray-400': !isSelected,
                                    })}
                                  >
                                    {icon}
                                  </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="truncate font-medium">{item[searchKey]}</span>
                                    {isSelected && (
                                      <div
                                        className={cn(
                                          'h-2 w-2 flex-shrink-0 rounded-full',
                                          colorClasses.dotSelected
                                        )}
                                      />
                                    )}
                                  </div>
                                </div>
                              </>
                            )}
                          </motion.button>
                        )
                      })}

                      {/* Use typed value when no items match */}
                      {onCustomSelect && searchQuery.trim() && filteredItems.length === 0 && (
                        <motion.button
                          onClick={handleCustomSubmit}
                          whileHover={{ backgroundColor: 'rgb(239 246 255)' }}
                          whileTap={{ scale: 0.98 }}
                          className="flex w-full items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-3 text-left transition-colors hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1"
                        >
                          <div className="flex-shrink-0">
                            <div className="h-4 w-4 rounded-full border-2 border-dashed border-blue-400" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <span className="truncate text-sm font-medium text-blue-700">
                              Use &ldquo;{searchQuery.trim()}&rdquo;
                            </span>
                          </div>
                        </motion.button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>,
          document.body
        )}

      {/* Trigger Button */}
      <motion.button
        ref={buttonRef}
        onClick={toggleDropdown}
        disabled={disabled || isLoading}
        whileHover={!(disabled || isLoading) ? { backgroundColor: 'rgb(243 243 243)' } : undefined}
        whileTap={!(disabled || isLoading) ? { scale: 0.98 } : undefined}
        className={cn(
          'flex w-full items-center gap-3 rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 text-base transition-colors',
          'focus-visible:border-[#B0B0B0] focus-visible:outline-none',
          {
            'cursor-not-allowed opacity-50': disabled || isLoading,
          }
        )}
      >
        <span
          className={cn('line-clamp-1 min-w-0 flex-1 text-left', {
            'text-gray-400': !selectedId,
            'text-black': selectedId,
          })}
        >
          {selectedId && selectedItem ? selectedItem[searchKey] : placeholder}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
          className="flex-shrink-0 text-gray-400"
        >
          <ChevronDown className="h-5 w-5" />
        </motion.div>
      </motion.button>
    </div>
  )
}
